import { useState } from 'react';
import { QueryClient, QueryClientProvider, useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import type { AdminBootstrapDictionaries, AdminCompany, AdminCompanyStatusHistory, AdminDictionaryItem, AdminListResult, CompanyStatusScope } from '../../../api/types';
import { adminApi } from '../adminApi';
import './CompanyAccessPanel.css';

interface Props {
  company: AdminCompany;
  dictionaries: AdminBootstrapDictionaries;
  onChange: (company: AdminCompany) => void;
}

const scopeNames: Record<CompanyStatusScope, string> = { company: 'Компания', purchase: 'Покупки', sale: 'Продажи' };

function CompanyAccessPanelContent({ company, dictionaries, onChange }: Props) {
  const [page, setPage] = useState(1);
  const [scope, setScope] = useState<CompanyStatusScope | ''>('');
  const companyId = company.company_id;
  const queryClient = useQueryClient();
  const historyQuery = useQuery<AdminListResult<AdminCompanyStatusHistory>, Error>({
    queryKey: ['company-status-history', companyId, page, scope, company.company_updated_at],
    queryFn: () => adminApi.getCompanyStatusHistory(companyId, page, scope || undefined),
  });
  const history = historyQuery.data ?? { items: [], meta: {} };
  const isLoading = historyQuery.isPending;
  const error = historyQuery.error?.message;
  const handleChange = (updated: AdminCompany): void => {
    onChange(updated);
    void queryClient.invalidateQueries({ queryKey: ['company-status-history', companyId] });
  };
  const hasWorkspaceAccess = dictionaries.company_statuses.find((status) => status.id === company.company_status_id)?.has_workspace_access === true;
  const statuses: Array<{ scope: CompanyStatusScope; id: number; options: AdminDictionaryItem[] }> = [
    { scope: 'company', id: company.company_status_id, options: dictionaries.company_statuses },
    { scope: 'purchase', id: company.company_purchase_status_id ?? 1, options: dictionaries.company_purchase_statuses ?? [] },
    { scope: 'sale', id: company.company_sale_status_id ?? 1, options: dictionaries.company_sale_statuses ?? [] },
  ];

  return <section className="admin-detail-card company-access-panel" aria-label="Статусы и доступы компании">
    <h2>Статусы и доступы</h2>
    <p>Покупки и продажи управляются отдельно. Блокировка или архив компании закрывают оба доступа.</p>
    <div className="company-access-panel__statuses">
      {statuses.map((status) => <StatusEditor key={`${companyId}-${status.scope}`} companyId={companyId} scope={status.scope} currentId={status.id} options={status.options} hasWorkspaceAccess={hasWorkspaceAccess} onChange={handleChange} />)}
    </div>
    <h3>История изменения статусов</h3>
    <label>Тип статуса <select value={scope} onChange={(event) => { setScope(event.target.value as CompanyStatusScope | ''); setPage(1); }}>
      <option value="">Все статусы</option>
      {Object.entries(scopeNames).map(([value, name]) => <option key={value} value={value}>{name}</option>)}
    </select></label>
    {error ? <p role="alert">{error}</p> : null}
    {isLoading ? <p role="status">Загружаем историю…</p> : <>
      {history.items.length === 0 ? <p>Изменений статусов пока нет.</p> : <ol className="company-access-panel__history">
        {history.items.map((entry) => <li key={entry.history_id}>
          <strong>{scopeNames[entry.history_scope]}: {entry.history_from_status_name ?? '—'} → {entry.history_to_status_name}</strong>
          <span>{new Date(entry.history_created_at).toLocaleString('ru-RU')} · {entry.history_actor_user_id === null ? 'Система' : `Пользователь #${entry.history_actor_user_id}`} · {entry.history_action_name}</span>
          {entry.history_comment ? <p>{entry.history_comment}</p> : null}
        </li>)}
      </ol>}
      <div className="company-access-panel__pagination">
        <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>Назад</button>
        <span>Страница {page} из {Math.max(1, history.meta.page_count ?? 1)}</span>
        <button type="button" disabled={page >= (history.meta.page_count ?? 1)} onClick={() => setPage(page + 1)}>Далее</button>
      </div>
    </>}
  </section>;
}

export function CompanyAccessPanel(props: Props) {
  const [queryClient] = useState(() => new QueryClient({ defaultOptions: { queries: { retry: false, staleTime: 0 } } }));
  return <QueryClientProvider client={queryClient}><CompanyAccessPanelContent {...props} /></QueryClientProvider>;
}

function StatusEditor({ companyId, scope, currentId, options, hasWorkspaceAccess, onChange }: { companyId: number; scope: CompanyStatusScope; currentId: number; options: AdminDictionaryItem[]; hasWorkspaceAccess: boolean; onChange: (company: AdminCompany) => void }) {
  const { register, handleSubmit, watch, reset } = useForm<{ target_status_id: number; comment: string }>({ values: { target_status_id: currentId, comment: '' } });
  const targetId = watch('target_status_id');
  const activeMutations = useIsMutating({ mutationKey: ['company-status', companyId] });
  const mutation = useMutation({
    mutationKey: ['company-status', companyId],
    mutationFn: (values: { target_status_id: number; comment: string }) => adminApi.changeCompanyStatus(companyId, { status_scope: scope, target_status_id: values.target_status_id, expected_status_id: currentId, comment: values.comment }),
    onSuccess: (updated: AdminCompany) => { onChange(updated); reset({ target_status_id: targetId, comment: '' }); },
  });
  const isSaving = mutation.isPending || activeMutations > 0;
  const error = mutation.error instanceof Error ? mutation.error.message : null;
  const current = options.find((status) => status.id === currentId);
  const isOpen = hasWorkspaceAccess && current?.value === true;
  const save = handleSubmit((values) => { if (!isSaving) mutation.mutate(values); });
  return <fieldset disabled={isSaving}>
    <legend>{scopeNames[scope]}</legend>
    <p><strong>{current?.name ?? 'Статус не загружен'}</strong>{scope === 'company' ? null : <span className={isOpen ? 'admin-status admin-status_offer_available' : 'admin-status admin-status_offer_unavailable'}>{isOpen ? 'Доступ открыт' : 'Доступ закрыт'}</span>}</p>
    <label>Статус {scopeNames[scope].toLocaleLowerCase('ru-RU')}<select {...register('target_status_id', { valueAsNumber: true })}>
      {options.map((status) => <option key={status.id} value={status.id}>{status.name}{scope === 'company' ? '' : status.value ? ' — доступ открыт' : ' — доступ закрыт'}</option>)}
    </select></label>
    <label>Комментарий к изменению {scopeNames[scope].toLocaleLowerCase('ru-RU')}<textarea {...register('comment')} rows={2} /></label>
    <button className="workspace-secondary-button" type="button" disabled={targetId === currentId || options.length === 0} onClick={save}>{isSaving ? 'Сохраняем…' : 'Сохранить статус'}</button>
    {error ? <p role="alert">{error}</p> : null}
  </fieldset>;
}
